/**
 * SubscriptionContext
 *
 * Provides subscription state and access checking for the user app.
 * Frontend checks are for UX only - actual security is enforced via RLS policies.
 *
 * @see docs/architecture/subscription-database-schema.md
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from '@xanectech/auth';
import { supabase } from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';
import type {
  ProductCode,
  ProductTier,
  ProductStatus,
  UserSubscription,
  SubscriptionStatus,
} from '@xanectech/types';

const logger = createLogger('Data - SubscriptionContext');

// ============================================
// Types
// ============================================

export interface UserProductAccess {
  product_code: ProductCode;
  product_tier: ProductTier;
  status: ProductStatus;
  is_trial: boolean;
  trial_end: string | null;
}

export interface SubscriptionContextType {
  /** Current user subscription (null if free tier) */
  subscription: UserSubscription | null;
  /** Products the user has access to */
  products: UserProductAccess[];
  /** Whether subscription data is loading */
  isLoading: boolean;
  /** Error if subscription fetch failed */
  error: Error | null;
  /** Check if user has access to a specific product */
  hasProductAccess: (productCode: ProductCode) => boolean;
  /** Check if user has access at or above a specific tier */
  hasTierAccess: (productCode: ProductCode, minTier: ProductTier) => boolean;
  /** Get the user's tier for a specific product */
  getProductTier: (productCode: ProductCode) => ProductTier | null;
  /** Check if user is on a trial for a product */
  isProductTrial: (productCode: ProductCode) => boolean;
  /** Refresh subscription data */
  refreshSubscription: () => Promise<void>;
}

// ============================================
// Context
// ============================================

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined,
);

// ============================================
// Tier Hierarchy
// ============================================

const TIER_HIERARCHY: Record<ProductTier, number> = {
  free: 0,
  plus: 1,
  bronze: 2,
  silver: 3,
  gold: 4,
  platinum: 5,
};

// ============================================
// Provider
// ============================================

interface SubscriptionProviderProps {
  children: ReactNode;
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(
    null,
  );
  const [products, setProducts] = useState<UserProductAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch subscription data from database
   */
  const fetchSubscription = useCallback(async () => {
    if (!user?.id) {
      setSubscription(null);
      setProducts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch user subscription
      const { data: subData, error: subError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (subError && subError.code !== 'PGRST116') {
        // PGRST116 = no rows found, which is fine (free tier user)
        throw subError;
      }

      // Map database response to UserSubscription type
      const userSub: UserSubscription | null = subData
        ? {
            id: subData.id,
            user_id: subData.user_id,
            status: subData.status as SubscriptionStatus,
            stripe_customer_id: subData.stripe_customer_id,
            stripe_subscription_id: subData.stripe_subscription_id,
            current_period_start: subData.current_period_start,
            current_period_end: subData.current_period_end,
            cancel_at_period_end: subData.cancel_at_period_end ?? false,
            canceled_at: subData.canceled_at ?? null,
            billing_cycle: subData.billing_cycle as 'monthly' | 'yearly',
            prepaid_months: subData.prepaid_months,
            prepaid_years: subData.prepaid_years,
            discount_applied: subData.discount_applied
              ? Number(subData.discount_applied)
              : null,
            created_at: subData.created_at,
            updated_at: subData.updated_at,
          }
        : null;

      setSubscription(userSub);

      // Fetch user's product access
      if (userSub) {
        const { data: productData, error: productError } = await supabase
          .from('user_subscription_products')
          .select(
            `
            id,
            status,
            trial_start,
            trial_end,
            subscription_products (
              product_code,
              product_tier
            )
          `,
          )
          .eq('subscription_id', userSub.id);

        if (productError) throw productError;

        const userProducts: UserProductAccess[] = (productData || []).map(
          (p) => {
            const product = p.subscription_products as unknown as {
              product_code: string;
              product_tier: string;
            } | null;
            const now = new Date();
            const trialEnd = p.trial_end ? new Date(String(p.trial_end)) : null;
            const isTrial =
              p.status === 'trial' && trialEnd !== null && trialEnd > now;

            return {
              product_code: (product?.product_code || 'core') as ProductCode,
              product_tier: (product?.product_tier || 'free') as ProductTier,
              status: p.status as ProductStatus,
              is_trial: isTrial,
              trial_end: p.trial_end,
            };
          },
        );

        setProducts(userProducts);
      } else {
        // Free tier user - has access to 'core' product at 'free' tier
        setProducts([
          {
            product_code: 'core',
            product_tier: 'free',
            status: 'active',
            is_trial: false,
            trial_end: null,
          },
        ]);
      }
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to fetch subscription');
      logger.error('Error fetching subscription:', { error });
      setError(error);
      // Default to core product on error
      setProducts([
        {
          product_code: 'core',
          product_tier: 'free',
          status: 'active',
          is_trial: false,
          trial_end: null,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Fetch subscription on mount and when user changes
  useEffect(() => {
    void fetchSubscription();
  }, [fetchSubscription]);

  /**
   * Check if user has access to a specific product
   * NOTE: This is for UX only - actual access is enforced by RLS
   */
  const hasProductAccess = useCallback(
    (productCode: ProductCode): boolean => {
      // Core is always accessible (free tier)
      if (productCode === 'core') return true;

      return products.some(
        (p) =>
          p.product_code === productCode &&
          (p.status === 'active' || p.status === 'trial'),
      );
    },
    [products],
  );

  /**
   * Check if user has access at or above a specific tier
   */
  const hasTierAccess = useCallback(
    (productCode: ProductCode, minTier: ProductTier): boolean => {
      const product = products.find((p) => p.product_code === productCode);

      if (!product) {
        // No product access - only free tier check passes
        return minTier === 'free';
      }

      if (product.status !== 'active' && product.status !== 'trial') {
        return false;
      }

      return TIER_HIERARCHY[product.product_tier] >= TIER_HIERARCHY[minTier];
    },
    [products],
  );

  /**
   * Get user's tier for a specific product
   */
  const getProductTier = useCallback(
    (productCode: ProductCode): ProductTier | null => {
      // Core always returns 'free' as base tier
      if (productCode === 'core') {
        const coreProduct = products.find((p) => p.product_code === 'core');
        return coreProduct?.product_tier || 'free';
      }

      const product = products.find((p) => p.product_code === productCode);
      return product?.product_tier || null;
    },
    [products],
  );

  /**
   * Check if user is on a trial for a product
   */
  const isProductTrial = useCallback(
    (productCode: ProductCode): boolean => {
      const product = products.find((p) => p.product_code === productCode);
      return product?.is_trial || false;
    },
    [products],
  );

  /**
   * Refresh subscription data
   */
  const refreshSubscription = useCallback(async () => {
    await fetchSubscription();
  }, [fetchSubscription]);

  const value = useMemo<SubscriptionContextType>(
    () => ({
      subscription,
      products,
      isLoading,
      error,
      hasProductAccess,
      hasTierAccess,
      getProductTier,
      isProductTrial,
      refreshSubscription,
    }),
    [
      subscription,
      products,
      isLoading,
      error,
      hasProductAccess,
      hasTierAccess,
      getProductTier,
      isProductTrial,
      refreshSubscription,
    ],
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

/**
 * Hook to access subscription context
 * Must be used within a SubscriptionProvider
 */
export function useSubscription(): SubscriptionContextType {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider',
    );
  }
  return context;
}
